import { useState, useCallback } from 'react';
import { Icon, Popover } from '@shopify/polaris';
import { ChevronDownIcon, TextFontIcon } from '@shopify/polaris-icons';

const COLORS = [
  ['transparent', '#000000', '#808080', '#A0522D', '#CD853F', '#D2B48C', '#F4A460', '#F5DEB3'],
  ['#FFE4B5', '#FFD700', '#FFFF00', '#FFFAFA', '#FFF5EE', '#F5F5F5', '#FFFFFF', '#FA8072'],
  ['#FF6347', '#FF0000', '#FF00FF', '#EE82EE', '#DDA0DD', '#DA70D6', '#D8BFD8', '#8A2BE2'],
  ['#6A5ACD', '#708090', '#4682B4', '#87CEEB', '#B0E0E6', '#40E0D0', '#00FFFF', '#00FF7F'],
  ['#32CD32', '#00FF00', '#9ACD32', '#2E8B57', '#008080', '#98FB98', '#FA8072', '#FFA07A'],
];

interface ColorPickerProps {
  onColorSelect: (color: string) => void;
  title: string;
  defaultColor?: string;
}

const ColorPicker = ({ onColorSelect, title, defaultColor }: ColorPickerProps) => (
  <div style={{ padding: '8px' }}>
    <div style={{ marginBottom: '8px', fontSize: '13px', textAlign: 'center' }}>
      <b>{title}</b>
    </div>
    {defaultColor && (
      <button
        onClick={() => onColorSelect(defaultColor)}
        style={{
          width: '100%',
          padding: '4px',
          marginBottom: '8px',
          border: '1px solid #ddd',
          borderRadius: '4px',
          background: 'white',
          cursor: 'pointer',
        }}
      >
        Reset to default
      </button>
    )}
    <div style={{ display: 'grid', gap: '4px' }}>
      {COLORS.map((row, rowIndex) => (
        <div key={rowIndex} style={{ display: 'flex', gap: '4px' }}>
          {row.map((color) => (
            <button
              key={color}
              onClick={() => onColorSelect(color)}
              style={{
                width: '18px',
                height: '18px',
                border: '1px solid #ddd',
                borderRadius: '4px',
                background: color,
                cursor: 'pointer',
              }}
              title={color}
            />
          ))}
        </div>
      ))}
    </div>
  </div>
);

export const ColorButton = ({ disabled }: { disabled?: boolean }) => {
  const [popoverActive, setPopoverActive] = useState(false);
  const togglePopoverActive = useCallback(() => setPopoverActive((active) => !active), []);

  const handleColorChange = (color: string) => {
    if (color === 'transparent') {
      document.execCommand('foreColor', false, 'rgba(255, 255, 255, 0)');
    } else {
      document.execCommand('foreColor', false, color);
    }
  };

  const handleBgColorChange = (color: string) => {
    document.execCommand('backColor', false, color);
  };

  const activator = (
    <button
      onClick={togglePopoverActive}
      style={{
        padding: '4px 6px',
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        borderRadius: '4px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: '4px',
      }}
      title="Change Color"
      disabled={disabled}
    >
      <Icon source={TextFontIcon} tone="base" />
      <Icon source={ChevronDownIcon} tone="base" />
    </button>
  );

  return (
    <Popover active={popoverActive} activator={activator} onClose={togglePopoverActive} preferredAlignment="right">
      <div
        style={{
          display: 'flex',
          padding: '8px',
          gap: '4px',
          borderBottom: '1px solid #ddd',
        }}
      >
        <ColorPicker title="Background Color" onColorSelect={handleBgColorChange} defaultColor="transparent" />
        <ColorPicker title="Text Color" onColorSelect={handleColorChange} defaultColor="#303030" />
      </div>
    </Popover>
  );
};

export default ColorButton;
